import namedColors from 'color-name-list';
import nearestColor from 'nearest-color';
import { validateHTMLColorHex } from 'validate-color';
import { clamp, isEqual } from 'lodash';
import { adjustHue, parseToHsl, parseToRgb, setLightness, setSaturation } from 'polished';
import { stepListDefault } from '../data/stepList';
import { colorHarmoniesList, colorDominanceList } from '../data/colorHarmoniesList';

let nearestNamedColor;

const colorToolsUtils = {
    isValidHex: (value) => {
        if (!value) {
            return null;
        }
        return value.length === 3 || value.length === 6;
    },

    isValidSteps: (value) => {
        const allowedSteps = [];

        for (let i = 0; i < stepListDefault.length; i += 1) {
            allowedSteps.push(stepListDefault[i].value);
        }

        return allowedSteps.includes(value);
    },

    isValidHarmony: (value) => {
        if (!value) {
            return null;
        }
        const renderValueToList = value.split(',').map(Number);
        return colorHarmoniesList.some((listItem) => isEqual(listItem, renderValueToList));
    },

    isValidHarmonyDominance: (value) => {
        if (!value) {
            return null;
        }
        const renderValueToList = value.split(',').map(Number);
        return colorDominanceList.some((listItem) => isEqual(listItem, renderValueToList));
    },

    generateColor: () =>
        Math.floor(Math.random() * 2 ** 24)
            .toString(16)
            .padStart(6, '0'),

    colorName: (value) => {
        const colorValueToHex = `#${value}`;
        let colorName;

        if (colorToolsUtils.isValidColor(value)) {
            if (!nearestNamedColor) {
                const namedColorsList = namedColors.reduce(
                    (o, { name, hex }) => Object.assign(o, { [name]: hex }),
                    {},
                );
                nearestNamedColor = nearestColor.from(namedColorsList);
            }

            const { name } = nearestNamedColor(colorValueToHex);
            colorName = name;
        }

        return colorName;
    },

    isValidColor: (value) => {
        let isValid = false;
        if (value && colorToolsUtils.isValidHex(value) && validateHTMLColorHex(`#${value}`)) {
            isValid = true;
        }
        return isValid;
    },

    colorInLibraryLabel: (isInLlibrary, theme, intl, user) => {
        return isInLlibrary
            ? {
                  colorLibraryLabel: intl.formatMessage({
                      id: 'removeFromLibrary.button.label',
                  }),
                  colorLibraryIcon: theme.site.feedback.icon.remove,
              }
            : {
                  colorLibraryLabel: intl.formatMessage({
                      id: user ? 'addToLibrary.button.label' : 'library.signUpToSave',
                  }),
                  colorLibraryIcon: theme.site.feedback.icon.add,
              };
    },

    setMainColorLabel: (theme, intl) => {
        return {
            setMainColorLabel: intl.formatMessage({
                id: 'setMainColor.button.label',
            }),
            setMainColorIcon: theme.site.feedback.icon.mainColor,
        };
    },

    deselectColorLabel: (theme, intl) => {
        return {
            deselectColorLabel: intl.formatMessage({
                id: 'deselectColor.button.label',
            }),
            deselectColorIcon: theme.site.feedback.icon.deselect,
        };
    },

    pop: (value) => {
        const { saturation, lightness } = parseToHsl(value);

        const popHue = adjustHue(210, setLightness(clamp(1 - lightness, 0.15, 0.85), value));

        if (saturation < 0.6 && saturation > 0.3) {
            return setSaturation(clamp(0.3 + saturation, 0, 1), popHue);
        }

        if (saturation < 0.3) {
            return setSaturation(clamp(0.6 + saturation, 0, 1), popHue);
        }

        return setSaturation(1.3 - saturation, popHue);
    },

    hexToHsl: (hex) => {
        const { hue, saturation, lightness } = parseToHsl(hex);
        return {
            h: Math.round(hue),
            s: Math.round(saturation * 100),
            l: Math.round(lightness * 100),
        };
    },

    hexToRgb: (hex) => {
        const { red, green, blue } = parseToRgb(hex);
        return {
            r: red,
            g: green,
            b: blue,
        };
    },
};

export default colorToolsUtils;
